import { useEffect, useState } from 'react';
import { Select, MenuItem, Checkbox, ListItemText, SelectChangeEvent } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useAppDispatch, useAppSelector } from '../../../../redux/hook';
import { fetchTasksQueueAssignableUsers } from '../../../../redux/slices/tasksQueueSlice';

import AuthService from '../../../../services/auth.service';

import { UserProfile } from '../../../../types/UserProfile';
import { ResourceType } from '../../../../types/ResourceType';
import { AssignableUser } from '../../../../types/TasksQueue';
import { TasksReaderQueue } from '../../../../constants/Constants';

import FormInputCollection from '../../../common/FormInputCollection/FormInputCollection';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '5px',
    backgroundColor: '#fff',
  },
  label: {
    margin: '0 10px 0 20px',
    width: 'fit-content',
    color: '#000000DE',
  },
  selectField: {
    width: '200px',
    height: '36px',
  },
  listContainer: {
    width: '240px',
    backgroundColor: '#fff',
  },
  checkbox: {
    '& .MuiCheckbox-root': {
      color: 'grey',
    },
    '& .MuiCheckbox-root.Mui-checked': {
      color: 'green',
    },
  },
  menu: {
    '& .MuiMenu-paper': {
      overflowY: 'auto',
    },
  },
  customList: {
    height: '500px',
  },
}));

const TasksQueueAssignedTo = ({ resetAssignedToSelection, handleAssignedUserToggle }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const tasksQueueState = useAppSelector((state) => state.tasksQueue);

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  useEffect(() => {
    AuthService.getUserProfile().then(setUserProfile);
  }, []);

  useEffect(() => {
    setSelectedUsers([]); // Clear selected users on reset
  }, [resetAssignedToSelection]);

  useEffect(() => {
    if (tasksQueueState.assignableUsers.length === 0) {
      dispatch(fetchTasksQueueAssignableUsers());
    }
  }, [tasksQueueState.assignableUsers]);

  const options: AssignableUser[] = [
    {
      resourceType: ResourceType.AppUser,
      id: userProfile?.UserId || '',
      label: TasksReaderQueue.AssignedToMe,
    },
    ...tasksQueueState.assignableUsers,
  ] as AssignableUser[];

  // Separate "Assigned to me" and sort the rest
  const assignedToMeOption = options.find((option) => option.id === (userProfile?.UserId || ''));
  const otherOptions = options.filter((option) => option.id !== (userProfile?.UserId || ''));
  const sortedOtherOptions = otherOptions.sort((a, b) => a.label.localeCompare(b.label));

  // Combine "Assigned to me" with the sorted options
  const sortedOptions = assignedToMeOption ? [assignedToMeOption, ...sortedOtherOptions] : sortedOtherOptions;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const selectedIds = event.target.value as string[];
    setSelectedUsers(selectedIds);

    // Find selected labels
    const selectedLabels = selectedIds.map((id) => {
      if (id === (userProfile?.UserId || '')) {
        return TasksReaderQueue.AssignedToMe;
      }
      return sortedOptions.find((user) => user.id === id)?.label || '';
    });

    // Handle special case where "Assigned to me" is selected
    const finalLabels = selectedLabels.map((label) => {
      if (label === TasksReaderQueue.AssignedToMe) {
        return userProfile?.given_name || '';
      }
      return label;
    });

    handleAssignedUserToggle(finalLabels);
  };

  return (
    <div className={classes.container}>
      <FormInputCollection label='Assigned To' style={{ marginTop: '10px' }}>
        <Select
          placeholder='Assigned To'
          className={classes.selectField}
          multiple
          value={selectedUsers}
          onChange={handleChange}
          MenuProps={{ classes: { list: classes.customList } }}
          renderValue={(selected) => {
            const selectedArray = selected as string[];
            if (selectedArray.length === 0) {
              return '';
            }

            // Map IDs to their labels for display
            const selectedLabels = selectedArray.map((id) => {
              if (id === (userProfile?.UserId || '')) {
                return TasksReaderQueue.AssignedToMe;
              }
              return sortedOptions.find((user) => user.id === id)?.label || '';
            });

            if (selectedLabels.length === 0) {
              return '';
            }
            return selectedLabels.length > 2 ? `${selectedLabels.slice(0, 2).join(', ')}, ...` : selectedLabels.join(', ');
          }}
        >
          {sortedOptions.map((option) => (
            <MenuItem className={classes.listContainer} key={option.id} value={option.id}>
              <Checkbox checked={selectedUsers.includes(option.id)} className={classes.checkbox} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormInputCollection>
    </div>
  );
};

export default TasksQueueAssignedTo;
